import { Dispatch, ReactNode, SetStateAction, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";

import { DeliveryStopPod } from "api/response";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { updateBackgroundLoadingState } from "store/store";
import { PodType } from "./components/MultistopPodTypeField";
import MultistopProofOfDeliveryDialog from "./MultistopProofOfDeliveryDialog";
import ProofOfDeliveryMenu from "./ProofOfDeliveryMenu";
import ProofOfPickupButton from "./ProofOfPickupButton";

export type MultistopProofActionsProps = Readonly<{
  shipmentId: string;
  setIsShipmentDelivered: Dispatch<SetStateAction<boolean>>;
  middleContent?: ReactNode;
}>;

type DialogState = {
  show: boolean;
  initialPodType: PodType;
  initialStopId?: number;
};

const CLOSED_DIALOG_STATE: DialogState = {
  show: false,
  initialPodType: "master",
};

export default function MultistopProofActions({
  shipmentId,
  setIsShipmentDelivered,
  middleContent,
}: MultistopProofActionsProps) {
  const queryClient = useQueryClient();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const [dialogState, setDialogState] = useState<DialogState>(CLOSED_DIALOG_STATE);

  const refetchQueries = useCallback(() => {
    actions.updateBackgroundLoadingState(true);
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(shipmentId) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(shipmentId) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentGeoDataDetails(shipmentId) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.deliveryStopsPod(shipmentId) });
    queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.shipmentInvoicesListing(shipmentId) });
  }, [actions, queryClient, shipmentId]);

  const openDialog = useCallback((params: { podType: PodType; stopId?: number }) => {
    setDialogState({
      show: true,
      initialPodType: params.podType,
      initialStopId: params.stopId,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState(CLOSED_DIALOG_STATE);
  }, []);

  const handleMasterSelect = useCallback(() => {
    openDialog({ podType: "master" });
  }, [openDialog]);

  const handleStopSelect = useCallback(
    (stop: DeliveryStopPod) => {
      openDialog({ podType: "per_stop", stopId: stop.stop_id });
    },
    [openDialog],
  );

  return (
    <>
      <ProofOfPickupButton isOffPlatform={false} />
      {middleContent}
      <ProofOfDeliveryMenu shipmentId={shipmentId} onMasterSelect={handleMasterSelect} onStopSelect={handleStopSelect} />
      <MultistopProofOfDeliveryDialog
        show={dialogState.show}
        onHide={closeDialog}
        shipmentId={shipmentId}
        initialPodType={dialogState.initialPodType}
        initialStopId={dialogState.initialStopId}
        setIsShipmentDelivered={setIsShipmentDelivered}
        refetchFn={refetchQueries}
      />
    </>
  );
}
